import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { PageHeader } from "~/components/PageHeader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useUpdateInquiryStatusMutation } from "~/lib/tanstack-query/hooks/mutations/updateInquiryStatus";
import { useAdvertiserInquiries } from "~/lib/tanstack-query/hooks/useAdvertiserInquiries";
import { cn } from "~/lib/utils";

export const Route = createFileRoute("/advertiser/_main/inquiries")({
  component: RouteComponent,
});

type StatusFilter =
  | "all"
  | "open"
  | "pending"
  | "confirmed"
  | "declined"
  | "withdrawn";

type InquiryStatus = Exclude<StatusFilter, "all">;

type InquiryWithDetails = NonNullable<
  Awaited<
    ReturnType<
      typeof import("~/lib/server-functions/get-advertiser-inquiries").getAdvertiserInquiries
    >
  >
>[number];

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  if (value == null || value === "") return null;
  return (
    <div className="grid grid-cols-[minmax(0,140px)_1fr] gap-2 py-1">
      <span className="text-black/70 text-sm">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

function ActionButton({
  children,
  onClick,
  disabled,
  variant = "outline",
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: "outline" | "solid";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "rounded-md cursor-pointer px-4 py-2 border-2 border-black",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        variant === "solid"
          ? "bg-black text-white hover:bg-black/80"
          : "bg-white text-black hover:bg-black hover:text-white",
      )}
    >
      {children}
    </button>
  );
}

function AdvertiserInquiryActions({
  inquiry,
  onStatusChange,
  isPending,
  errorMessage,
  onRetry,
}: {
  inquiry: InquiryWithDetails;
  onStatusChange: (status: InquiryStatus) => void;
  isPending: boolean;
  errorMessage: string | null;
  onRetry: (() => void) | null;
}) {
  const status = inquiry.status;

  if (status === "withdrawn") {
    return (
      <p className="text-sm text-black/70">
        Diese Anfrage wurde vom Antragsteller zurückgezogen.
      </p>
    );
  }

  return (
    <div className="space-y-3 pt-2">
      <div className="flex flex-wrap gap-2">
        {(status === "open" || status === "confirmed") && (
          <ActionButton
            disabled={isPending}
            onClick={() => onStatusChange("pending")}
          >
            In Bearbeitung setzen
          </ActionButton>
        )}
        {(status === "open" || status === "pending") && (
          <>
            <ActionButton
              variant="solid"
              disabled={isPending}
              onClick={() => onStatusChange("confirmed")}
            >
              Bestätigen
            </ActionButton>
            <ActionButton
              disabled={isPending}
              onClick={() => onStatusChange("declined")}
            >
              Ablehnen
            </ActionButton>
          </>
        )}
        {status === "confirmed" && (
          <ActionButton
            disabled={isPending}
            onClick={() => onStatusChange("declined")}
          >
            Ablehnen
          </ActionButton>
        )}
        {status === "declined" && (
          <ActionButton
            disabled={isPending}
            onClick={() => onStatusChange("open")}
          >
            Ablehnung zurückziehen
          </ActionButton>
        )}
      </div>
      {errorMessage && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 text-sm text-red-700"
        >
          <span>{errorMessage}</span>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              disabled={isPending}
              className="underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Erneut versuchen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function InquiryDetails({
  inquiry,
  formatDateTime,
  onStatusChange,
  isStatusPending,
  statusErrorMessage,
  onRetryStatusChange,
}: {
  inquiry: InquiryWithDetails;
  formatDateTime: (d: Date | null | undefined) => string;
  onStatusChange: (status: InquiryStatus) => void;
  isStatusPending: boolean;
  statusErrorMessage: string | null;
  onRetryStatusChange: (() => void) | null;
}) {
  const appointments =
    typeof inquiry.appointments === "string" && inquiry.appointments
      ? (() => {
          try {
            const parsed = JSON.parse(inquiry.appointments) as Array<{
              start?: string;
              end?: string;
            }>;
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        })()
      : [];

  const requestedRooms = Array.isArray(inquiry.requestedRooms)
    ? inquiry.requestedRooms
    : [];

  return (
    <div className="max-w-2xl space-y-4">
      <h4 className="font-semibold text-sm uppercase tracking-wide text-black/70">
        Anfrage-Details
      </h4>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-0">
          <DetailRow
            label="Angelegt"
            value={formatDateTime(inquiry.createdAt)}
          />
          <DetailRow
            label="Location"
            value={inquiry.locationName ?? undefined}
          />
          <DetailRow label="Anrede" value={inquiry.salutation ?? undefined} />
          <DetailRow label="Vorname" value={inquiry.firstName} />
          <DetailRow label="Nachname" value={inquiry.lastName} />
          <DetailRow label="Unternehmen" value={inquiry.company ?? undefined} />
          <DetailRow label="E-Mail" value={inquiry.email} />
          <DetailRow label="Telefon" value={inquiry.telephone} />
        </div>
        <div className="space-y-0">
          <DetailRow label="Art" value={inquiry.businessType ?? undefined} />
          <DetailRow
            label="Event-Typ"
            value={inquiry.eventTypeName ?? undefined}
          />
          <DetailRow
            label="Anzahl Zimmer"
            value={inquiry.numberOfRooms ?? undefined}
          />
          <DetailRow
            label="Anzahl Gäste"
            value={inquiry.numberOfGuests ?? undefined}
          />
          <DetailRow
            label="Budget"
            value={
              inquiry.budget != null
                ? `${inquiry.budgetType === "Pro Person" ? "pro Person: " : ""}${inquiry.budget} ${inquiry.budgetCurrency ?? "EUR"}`
                : undefined
            }
          />
          {requestedRooms.length > 0 && (
            <div className="grid grid-cols-[minmax(0,140px)_1fr] gap-2 py-1">
              <span className="text-black/70 text-sm">Gewünschte Zimmer</span>
              <span className="text-sm">
                {requestedRooms.map((r) => r.name).join(", ")}
              </span>
            </div>
          )}
        </div>
      </div>
      {appointments.length > 0 && (
        <div>
          <span className="text-black/70 text-sm block py-1">
            Terminvorschläge
          </span>
          <ul className="list-disc list-inside text-sm space-y-1">
            {appointments.map((a, i) => {
              const start = a.start ? new Date(a.start) : null;
              const end = a.end ? new Date(a.end) : null;
              const startStr =
                start && !Number.isNaN(start.getTime())
                  ? formatDateTime(start)
                  : "";
              const endStr =
                end && !Number.isNaN(end.getTime()) ? formatDateTime(end) : "";
              return (
                <li key={i}>
                  {startStr}
                  {startStr && endStr ? " – " : ""}
                  {endStr}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {inquiry.notes ? (
        <DetailRow label="Anmerkungen" value={inquiry.notes} />
      ) : null}
      <div>
        <h4 className="font-semibold text-sm uppercase tracking-wide text-black/70 mb-2">
          Aktionen
        </h4>
        <AdvertiserInquiryActions
          inquiry={inquiry}
          onStatusChange={onStatusChange}
          isPending={isStatusPending}
          errorMessage={statusErrorMessage}
          onRetry={onRetryStatusChange}
        />
      </div>
    </div>
  );
}

function RouteComponent() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [selectedLocationId, setSelectedLocationId] = useState<string>("all");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [statusUpdateError, setStatusUpdateError] = useState<{
    inquiryId: number;
    message: string;
    retryStatus: InquiryStatus;
  } | null>(null);
  const updateInquiryStatusMutation = useUpdateInquiryStatusMutation();

  const { data: allInquiries } = useAdvertiserInquiries();

  const filters: {
    locationId?: number;
    status?: InquiryStatus;
  } = {};
  if (selectedLocationId !== "all") {
    filters.locationId = Number(selectedLocationId);
  }
  if (statusFilter !== "all") {
    filters.status = statusFilter;
  }

  const { data: inquiries, isPending } = useAdvertiserInquiries(
    Object.keys(filters).length > 0 ? filters : undefined,
  );

  const formatDate = (date: Date | null | undefined) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDateTime = (date: Date | null | undefined) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      open: "Offen",
      pending: "In Bearbeitung",
      confirmed: "Bestätigt",
      declined: "Abgelehnt",
      withdrawn: "Zurückgezogen",
    };
    return statusMap[status] || status;
  };

  const statusFilters: { value: StatusFilter; label: string }[] = [
    { value: "all", label: "Alle" },
    { value: "open", label: "Offen" },
    { value: "pending", label: "In Bearbeitung" },
    { value: "confirmed", label: "Bestätigt" },
    { value: "declined", label: "Abgelehnt" },
    { value: "withdrawn", label: "Zurückgezogen" },
  ];

  const locations = useMemo(() => {
    if (!allInquiries) return [];
    const locationMap = new Map<number, { id: number; name: string }>();
    allInquiries.forEach((inquiry) => {
      if (inquiry.locationId && inquiry.locationName) {
        if (!locationMap.has(inquiry.locationId)) {
          locationMap.set(inquiry.locationId, {
            id: inquiry.locationId,
            name: inquiry.locationName,
          });
        }
      }
    });
    return Array.from(locationMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [allInquiries]);

  const toggleExpanded = (inquiryId: number) => {
    setExpandedId((id) => (id === inquiryId ? null : inquiryId));
  };

  const handleStatusFilterChange = (value: StatusFilter) => {
    setStatusFilter(value);
    setExpandedId(null);
  };

  const handleLocationFilterChange = (value: string) => {
    setSelectedLocationId(value);
    setExpandedId(null);
  };

  const handleStatusChange = async (
    inquiryId: number,
    status: InquiryStatus,
  ) => {
    try {
      await updateInquiryStatusMutation.mutateAsync({ inquiryId, status });
      setStatusUpdateError((prev) =>
        prev?.inquiryId === inquiryId ? null : prev,
      );
    } catch (err) {
      const message =
        err instanceof Error && err.message.trim()
          ? err.message
          : "Status konnte nicht aktualisiert werden.";
      setStatusUpdateError({
        inquiryId,
        message,
        retryStatus: status,
      });
    }
  };

  return (
    <div className="w-full mb-6">
      <PageHeader title="Meine Anfragen" />
      <div className="flex flex-col gap-2">
        {locations.length > 0 && (
          <div className="mb-4">
            <Select
              value={selectedLocationId}
              onValueChange={handleLocationFilterChange}
            >
              <SelectTrigger className="w-full sm:w-[300px]" variant="outline">
                <SelectValue placeholder="Location auswählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Locations</SelectItem>
                {locations.map((location) => (
                  <SelectItem key={location.id} value={String(location.id)}>
                    {location.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex flex-wrap gap-2 mb-4">
          {statusFilters.map((filter) => (
            <button
              type="button"
              key={filter.value}
              className={cn(
                "cursor-pointer px-5 py-2 border-2 border-black",
                statusFilter === filter.value
                  ? "bg-black text-white"
                  : "bg-white text-black",
              )}
              onClick={() => handleStatusFilterChange(filter.value)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="text-left">
                <th className="px-4 py-2 font-semibold w-12">&nbsp;</th>
                <th className="px-4 py-2 font-semibold">Datum</th>
                <th className="px-4 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 font-semibold">Name</th>
                <th className="px-4 py-2 font-semibold">E-Mail</th>
              </tr>
            </thead>
            <tbody>
              {isPending ? (
                <tr>
                  <td className="px-4 py-2 text-center" colSpan={5}>
                    Lädt...
                  </td>
                </tr>
              ) : inquiries && inquiries.length > 0 ? (
                inquiries.map((inquiry) => {
                  const statusLabel = getStatusLabel(inquiry.status);
                  const isExpanded = expandedId === inquiry.id;

                  return (
                    <Fragment key={inquiry.id}>
                      <tr className={cn(isExpanded && "border-b-0")}>
                        <td className="px-4 py-2 align-middle">
                          <button
                            type="button"
                            onClick={() => toggleExpanded(inquiry.id)}
                            className="inline-flex items-center justify-center p-1 rounded hover:bg-black/10 cursor-pointer"
                            aria-expanded={isExpanded}
                            aria-label={
                              isExpanded
                                ? `Details zu Anfrage von ${inquiry.firstName} ${inquiry.lastName} schließen`
                                : `Details zu Anfrage von ${inquiry.firstName} ${inquiry.lastName} anzeigen`
                            }
                          >
                            {isExpanded ? (
                              <ChevronDown className="size-5" />
                            ) : (
                              <ChevronRight className="size-5" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-2 align-middle">
                          {formatDate(inquiry.createdAt)}
                        </td>
                        <td className="px-4 py-2 align-middle">
                          <span className="font-bold">{statusLabel}</span>
                        </td>
                        <td className="px-4 py-2 align-middle">
                          <div className="flex flex-col">
                            <span className="font-semibold">
                              {inquiry.firstName} {inquiry.lastName}
                            </span>
                            {inquiry.company && (
                              <span className="text-sm text-black">
                                {inquiry.company}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2 align-middle">
                          {inquiry.email || "-"}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-4 py-4 bg-black/5 align-top"
                          >
                            <InquiryDetails
                              inquiry={inquiry}
                              formatDateTime={formatDateTime}
                              isStatusPending={
                                updateInquiryStatusMutation.isPending
                              }
                              statusErrorMessage={
                                statusUpdateError?.inquiryId === inquiry.id
                                  ? statusUpdateError.message
                                  : null
                              }
                              onRetryStatusChange={
                                statusUpdateError?.inquiryId === inquiry.id
                                  ? () =>
                                      handleStatusChange(
                                        inquiry.id,
                                        statusUpdateError.retryStatus,
                                      )
                                  : null
                              }
                              onStatusChange={(status) =>
                                handleStatusChange(inquiry.id, status)
                              }
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              ) : (
                <tr>
                  <td className="px-4 py-2 text-center" colSpan={5}>
                    Keine Anfragen gefunden.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
